import { useEffect, useRef } from 'react';

import { AllowType } from './Dropzone.types';
import classNames from 'classnames';
import { validateFile } from '@components/DropZone/utils';
import { useTranslation } from '@i18n/client';
import { toast } from 'react-toastify';
import { useAlert } from '@context/AlertContext';

type Props = {
  onFilesSelected: (files: File[]) => void;
  width: number | string;
  height: number | string;
  children?: React.ReactElement;
  allowTypes?: Array<AllowType>;
  type?: 'default' | 'dangerous';
  limit?: number;
  files: File[];
  disabled?: boolean;
};

export const allowedMimeTypes: { [key in AllowType]: string } = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
};

const DropZone: React.FC<Props> = ({
  files = [],
  onFilesSelected,
  width,
  height,
  children,
  allowTypes,
  type,
  limit = 5,
  disabled,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const alert = useAlert();

  // Problem is that FileList is readonly, so we can not actualize input files list. Only wipe it out completely
  useEffect(() => {
    if (!fileInputRef?.current?.files) return;
    if (!files.length) fileInputRef.current.value = '';
  }, [files]);

  const handleFilesSelected = async (newFiles: FileList) => {
    const filesArray = files.concat(Array.from(newFiles));

    if (!allowTypes) {
      if (filesArray.length > limit) {
        toast.error(t('common:file.tooMuchFiles', { limit }));
      }
      onFilesSelected(filesArray.slice(0, limit));
      return;
    }

    const results = await Promise.all(
      filesArray.map(validateFile.bind(this, t))
    );

    let validFiles = results
      .filter((result) => {
        if (result.reason && !result.reasonKey) {
        } else if (result.reasonKey) {
          alert.alert({
            message: t('common:attention'),
            description: result.reason,
            confirmButtonLabel: t('common:button.close'),
          });
        }
        return result.isValid;
      })
      .map((result) => result.file);

    if (validFiles.length > limit) {
      validFiles = validFiles.slice(0, limit);
      toast.error(t('common:file.tooMuchFiles', { limit }));
    }

    onFilesSelected(validFiles);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length) {
      handleFilesSelected(files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length) {
      handleFilesSelected(files);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      onClick={disabled ? undefined : handleClick}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className={classNames(
        'h-full overflow-hidden rounded-2xl border-dropzone-border border-dashed border-accent-primary',
        { 'border-status-text-error': type === 'dangerous' },
        { 'cursor-pointer': !disabled }
      )}
      style={{ width, height: height }}
    >
      <input
        ref={fileInputRef}
        type='file'
        multiple
        className='hidden'
        onChange={handleFileInput}
      />
      <div
        className={classNames('h-full bg-bg-layer', {
          '!bg-status-text-error': type === 'dangerous',
        })}
      >
        {children}
      </div>
    </div>
  );
};

export default DropZone;
